import { useState, useEffect, useCallback, useRef } from "react"; // Add useCallback and useRef

let refreshTokenTimeoutId;
// Refresh the token 1 minute before it expires
const TOKEN_REFRESH_MARGIN = 60000;

export const useAuth = () => {
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessToken")
  );

  const logout = useCallback(() => {
    localStorage.removeItem("accessToken");
    setAccessToken(null);
    if (refreshTokenTimeoutId) {
      clearTimeout(refreshTokenTimeoutId);
    }
  }, [setAccessToken]); // setAccessToken is stable

  // Use useRef for functions involved in mutual recursion or to ensure stable references for setTimeout
  const refreshAccessTokenRef = useRef(null);
  const setupRefreshTokenRef = useRef(null);

  refreshAccessTokenRef.current = useCallback(async () => {
    try {
      const currentToken = localStorage.getItem("accessToken"); // Or use the accessToken from state
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`, // Use currentToken or accessToken state
          },
        }
      );

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      const { accessToken: newAccessToken, expiresIn } = data;

      if (!newAccessToken) {
        throw new Error("No new access token received from refresh endpoint");
      }

      localStorage.setItem("accessToken", newAccessToken);
      setAccessToken(newAccessToken);

      if (setupRefreshTokenRef.current) {
        setupRefreshTokenRef.current(expiresIn);
      }
      return newAccessToken;
    } catch (error) {
      console.error("Token refresh error:", error);
      logout();
      throw error;
    }
  }, [accessToken, setAccessToken, logout]); // Include accessToken if used in fetch, logout

  setupRefreshTokenRef.current = useCallback((expiresIn) => {
    if (refreshTokenTimeoutId) {
      clearTimeout(refreshTokenTimeoutId);
    }
    const timeout = expiresIn * 1000 - TOKEN_REFRESH_MARGIN;
    if (refreshAccessTokenRef.current) {
      refreshTokenTimeoutId = setTimeout(
        refreshAccessTokenRef.current,
        timeout
      );
    }
  }, []); // This callback itself doesn't depend on refreshAccessTokenRef directly in deps,
  // but it uses its .current value. refreshAccessTokenRef.current is updated above.

  const login = useCallback(
    async (email, password) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/login`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${btoa(`${email}:${password}`)}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Authentication failed");
        }

        const data = await response.json();
        const { accessToken: returnedToken, expiresIn } = data;

        if (!returnedToken) {
          throw new Error("No access token received from login endpoint");
        }

        localStorage.setItem("accessToken", returnedToken);
        setAccessToken(returnedToken);

        if (setupRefreshTokenRef.current) {
          setupRefreshTokenRef.current(expiresIn);
        }
        return data;
      } catch (error) {
        console.error("Login error:", error);
        throw error;
      }
    },
    [setAccessToken]
  ); // Depends on setAccessToken and calls setupRefreshTokenRef.current

  const authenticatedFetch = useCallback(
    async (url, options = {}) => {
      const currentToken = localStorage.getItem("accessToken"); // Or use accessToken state
      const headers = {
        ...options.headers,
        Authorization: `Bearer ${currentToken}`, // Use currentToken or accessToken state
      };

      let response = await fetch(url, { ...options, headers });

      if (response.status === 401) {
        try {
          if (refreshAccessTokenRef.current) {
            const newToken = await refreshAccessTokenRef.current();
            headers.Authorization = `Bearer ${newToken}`;
            response = await fetch(url, { ...options, headers });
          } else {
            throw new Error("refreshAccessToken function is not available");
          }
        } catch (error) {
          throw error;
        }
      }
      return response;
    },
    [accessToken]
  ); // Include accessToken if used for the token

  useEffect(() => {
    return () => {
      if (refreshTokenTimeoutId) {
        clearTimeout(refreshTokenTimeoutId);
      }
    };
  }, []); // Empty dependency array, runs on mount and unmount.

  return {
    accessToken,
    login,
    logout,
    authenticatedFetch,
  };
};
